import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Loader2, ThumbsUp, MessageCircle, ArrowRight, FileText, Video, Music, File, Image as ImageIcon } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const resolveUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('/')) return `${BACKEND_URL}${url}`;
  return url;
};

const TYPE_META = {
  pdf: { icon: FileText, label: 'PDF' },
  video: { icon: Video, label: 'Vidéo' },
  audio: { icon: Music, label: 'Podcast' },
  document: { icon: File, label: 'Document' },
  image: { icon: ImageIcon, label: 'Image' },
};

/* Brief LOT3 — page publique dédiée d'un produit boutique partagé.
   Consultable SANS compte : les liens de partage externes pointent ici. */
export default function PublicProductPage() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/public/product/${productId}`)
      .then(({ data }) => { if (alive) setData(data); })
      .catch(() => { if (alive) setError(true); });
    return () => { alive = false; };
  }, [productId]);

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 px-4" data-testid="public-product-notfound">
        <p className="text-gray-600 mb-4">Ce contenu n&apos;est plus disponible.</p>
        <Link to="/" className="text-purple-600 font-medium hover:underline">Découvrir OpenSapiens →</Link>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="h-8 w-8 animate-spin text-purple-500" />
      </div>
    );
  }

  const { product, seller, seller_id } = data;
  const meta = TYPE_META[product.content_type] || TYPE_META.document;
  const Icon = meta.icon;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="public-product-page">
      <header className="bg-white border-b border-gray-100">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="text-lg font-bold bg-gradient-to-r from-cyan-500 via-purple-500 to-pink-500 bg-clip-text text-transparent">
            OpenSapiens
          </Link>
          <button
            onClick={() => navigate('/signup')}
            className="text-sm font-medium text-white px-4 py-1.5 rounded-lg hover:opacity-90"
            style={{ backgroundColor: '#7F77DD' }}
            data-testid="public-product-signup-btn"
          >
            Rejoindre
          </button>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-8">
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          {/* Cover */}
          <div className="relative h-56 bg-gradient-to-br from-gray-800 to-gray-900">
            {product.thumbnail_url ? (
              <img src={resolveUrl(product.thumbnail_url)} alt={product.title} className="w-full h-full object-cover object-center" />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Icon className="h-20 w-20 text-white/40" />
              </div>
            )}
            <div className="absolute bottom-3 left-3 px-2 py-1 rounded-lg text-xs font-medium flex items-center gap-1 bg-white/95 shadow-md">
              <Icon className="h-3.5 w-3.5 text-purple-600" />
              <span className="text-purple-700">{meta.label}</span>
            </div>
            <div className={`absolute top-3 right-3 px-3 py-1 rounded-full text-sm font-semibold text-white ${product.is_free ? 'bg-green-500' : 'bg-gradient-to-r from-purple-500 to-pink-500'}`}>
              {product.is_free ? 'Gratuit' : `${product.price} ${product.currency}`}
            </div>
          </div>

          <div className="p-6 space-y-4">
            <h1 className="text-xl font-bold text-gray-900" data-testid="public-product-title">{product.title}</h1>
            {product.description ? (
              <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{product.description}</p>
            ) : (
              <p className="text-sm text-gray-400 italic">Aucune description fournie.</p>
            )}
            <div className="flex items-center gap-4 text-sm text-gray-500">
              <span className="inline-flex items-center gap-1"><ThumbsUp className="h-4 w-4" /> {product.like_count || 0}</span>
              <span className="inline-flex items-center gap-1"><MessageCircle className="h-4 w-4" /> {product.comment_count || 0}</span>
            </div>

            {/* Seller */}
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
              {seller.avatar ? (
                <img src={resolveUrl(seller.avatar)} alt="" className="h-10 w-10 rounded-full object-cover" />
              ) : (
                <div className="h-10 w-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 text-white flex items-center justify-center font-bold text-sm">
                  {(seller.display_name || '?').substring(0, 2).toUpperCase()}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-900" data-testid="public-product-seller">{seller.display_name}</p>
                {seller.expert_pitch && <p className="text-xs text-gray-500 line-clamp-1">{seller.expert_pitch}</p>}
              </div>
              {seller.handle && (
                <a
                  href={`${window.location.origin}/api/@${seller.handle}`}
                  className="text-xs text-purple-600 font-medium hover:underline shrink-0"
                >
                  Voir le profil →
                </a>
              )}
            </div>

            {/* CTA */}
            <div className="flex items-center gap-3 flex-wrap pt-2">
              <button
                onClick={() => navigate(`/app/shop/${seller_id}?product=${product.product_id}`)}
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-white px-5 py-2.5 rounded-xl hover:opacity-90"
                style={{ backgroundColor: '#7F77DD' }}
                data-testid="public-product-open-app"
              >
                {product.is_free ? 'Télécharger dans OpenSapiens' : 'Voir dans OpenSapiens'} <ArrowRight className="h-4 w-4" />
              </button>
              <button
                onClick={() => navigate('/signup')}
                className="text-sm font-medium text-purple-600 border border-purple-300 px-5 py-2.5 rounded-xl hover:bg-purple-50"
              >
                Créer un compte gratuit
              </button>
            </div>
          </div>
        </div>

        <p className="text-center text-xs text-gray-400 mt-6">Propulsé par OpenSapiens</p>
      </main>
    </div>
  );
}
